import { PrimaryButton, Stack, Text } from "@certn/cascadia";
import { Schemas } from "../../../types";
import {
  Divider,
  ExpandableCard,
  LinkButton,
  Select,
  SelectItem,
  TextAreaFormikField,
  TextInputFormikField,
  Toggle,
  ToggleField,
} from "@certn/cascadia-react";
import { useState } from "react";
import { Key } from "react-aria-components";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import {
  FIELD_TYPE_CONFIGS,
  FIELD_TYPE_OPTIONS,
  FieldTypeKey,
  getConditionalQuestions,
  getFieldTypeKeyFromConfig,
  UNTITLED_QUESTION,
} from "./utils";
import { css } from "@emotion/css";
import { CustomQuestionnaireConditionalLogic } from "./CustomQuestionnaireConditionalLogic";
import { every, isEmpty, isNil, negate } from "lodash";
import { CustomQuestionnaireTemplateFormProps } from "./CustomQuestionnaireTemplateForm";
import { CustomQuestionnaireQuestionMenu } from "./CustomQuestionnaireQuestionMenu";

export type CustomQuestionnaireTemplateQuestionFieldProps =
  CustomQuestionnaireTemplateFormProps & {
    currentQuestion: Schemas["CustomQuestionnaireQuestionInput"];
    index: number;
  };

export const CustomQuestionnaireTemplateQuestionField = ({
  currentQuestion,
  index,
  values,
  setValues,
  setFieldValue,
  type,
}: CustomQuestionnaireTemplateQuestionFieldProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);

  const conditions = currentQuestion.display_conditions ?? [];

  const title = currentQuestion.external_name || UNTITLED_QUESTION;

  const canShowConditional =
    values.questions.length >= 2 &&
    getConditionalQuestions(values.questions, currentQuestion).length > 0;

  const deleteQuestion = (id?: string) => {
    if (!id) {
      throw new Error("Cannot delete question. Missing required field: ID.");
    }

    setValues((prev) => ({
      ...prev,
      questions: prev.questions
        .filter((q) => q.id !== id)
        .map((q, i) => ({
          ...q,
          display_priority: i + 1,
          display_conditions: (q.display_conditions ?? []).filter(
            (c) => c.parent_question_id !== currentQuestion.id
          ),
        })),
    }));
  };

  const moveQuestion = (
    questions: Schemas["CustomQuestionnaireQuestionInput"][],
    currentPriority: number,
    direction: "up" | "down"
  ) => {
    const delta = direction === "up" ? -1 : 1;
    const targetPriority = currentPriority + delta;
    const targetExists = questions.some(
      (i) => i.display_priority === targetPriority
    );

    if (!targetExists) {
      return questions;
    }

    const updatedQuestions = questions.map((question) => {
      if (question.display_priority === currentPriority) {
        return { ...question, display_priority: targetPriority };
      }

      if (question.display_priority === targetPriority) {
        return { ...question, display_priority: currentPriority };
      }

      return question;
    });

    setValues((prev) => ({
      ...prev,
      questions: updatedQuestions,
    }));
  };

  const duplicateQuestion = (
    questions: Schemas["CustomQuestionnaireQuestionInput"][],
    currentPriority: number
  ) => {
    const question = questions.find(
      (i) => i.display_priority === currentPriority
    );

    if (!question) return questions;

    const duplicatedQuestion = {
      ...question,
      id: uuidv4(),
      display_priority: questions.length + 1,
    };

    const updatedQuestions = [...questions, duplicatedQuestion];

    setValues((prev) => ({
      ...prev,
      questions: updatedQuestions,
    }));
  };

  const handleQuestion = (input: {
    action: "moveUp" | "moveDown" | "duplicate" | "delete";
    questions: Schemas["CustomQuestionnaireQuestionInput"][];
    currentPriority: number;
  }) => {
    const { action, questions, currentPriority } = input;

    switch (action) {
      case "moveUp":
        return moveQuestion(questions, currentPriority, "up");

      case "moveDown":
        return moveQuestion(questions, currentPriority, "down");
      case "duplicate":
        return duplicateQuestion(questions, currentPriority);
      case "delete":
        return deleteQuestion(currentQuestion.id);
    }
  };

  const handleDone = (current: Schemas["CustomQuestionnaireQuestionInput"]) => {
    // Check if the current question's field type was updated to a non-List type.
    // If yes, it can no longer be accepted as conditional questions and should be removed.
    const key = getFieldTypeKeyFromConfig({
      data_type: current.data_type,
      selectable_options: current.selectable_options || [],
      display_options: current.display_options || undefined,
    });

    const isNoLongerList = key === "text_field" || key === "multi_line_text";

    if (isNoLongerList) {
      const linkedConditionalQuestionIds = values.questions
        .filter((q) =>
          q.display_conditions?.find((c) => c.parent_question_id === current.id)
        )
        .map((q) => q.id);

      if (linkedConditionalQuestionIds.length > 0) {
        setValues((prev) => ({
          ...prev,
          questions: prev.questions.map((q) =>
            linkedConditionalQuestionIds.includes(q.id)
              ? {
                  ...q,
                  display_conditions: q.display_conditions?.filter(
                    (c) => c.parent_question_id !== current.id
                  ),
                }
              : q
          ),
        }));
      }
    }

    setExpanded(false);
  };

  return (
    <ExpandableCard
      border
      expanded={expanded}
      onToggle={() => setExpanded((prev) => !prev)}
      title={
        <Text
          style="global/heading-medium"
          className={css({
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: "1",
            overflow: "hidden",
            textOverflow: "ellipsis",
          })}
        >
          {currentQuestion.display_priority}. {title}
        </Text>
      }
      cardStyles={{
        expandedStyles: {
          titleBackgroundColor: "background-accent-neutral-weakest",
        },
      }}
      rightContent={
        <CustomQuestionnaireQuestionMenu
          type={type}
          index={index}
          currentPriority={currentQuestion.display_priority ?? 0}
          questions={values.questions}
          handleQuestion={handleQuestion}
        />
      }
    >
      <Stack
        gap="space-5"
        className={css({
          paddingRight: "var(--space-4)",
          paddingLeft: "var(--space-4)",
        })}
      >
        <ToggleField
          name={`questions.${index}.is_optional`}
          label={t("Make optional")}
          testID={`question-optional-${index}`}
          isDisabled={type === "update"}
        >
          {t("Make optional")}
        </ToggleField>
        <Select
          label={t("Field type")}
          isRequired
          isDisabled={type === "update"}
          testID={`question-field-type-${index}`}
          defaultValue={getFieldTypeKeyFromConfig({
            data_type: currentQuestion.data_type,
            selectable_options: currentQuestion.selectable_options || [],
            display_options: currentQuestion.display_options || undefined,
          })}
          onSelectionChange={(key: Key | null) => {
            if (key && typeof key === "string") {
              const fieldType = key as FieldTypeKey;

              const config = FIELD_TYPE_CONFIGS[fieldType];

              setValues((prev) => ({
                ...prev,
                questions: prev.questions.map((q) =>
                  q.id === currentQuestion.id
                    ? {
                        ...q,
                        data_type: config.data_type,
                        selectable_options: config.selectable_options,
                        display_options: config.display_options,
                      }
                    : {
                        ...q,
                        // Enforce that the conditional logic should get updated for all linked conditional questions
                        // that are dependant on the current question.
                        display_conditions: q.display_conditions?.map((c) =>
                          c.parent_question_id === currentQuestion.id
                            ? { ...c, condition_value: undefined }
                            : c
                        ),
                      }
                ),
              }));
            }
          }}
        >
          {FIELD_TYPE_OPTIONS.map((option) => (
            <SelectItem
              key={option.key}
              id={option.key}
              textValue={option.label}
            >
              {option.label}
            </SelectItem>
          ))}
        </Select>
        <TextInputFormikField
          name={`questions.${index}.external_name`}
          label={t("Field label")}
          isRequired
          testID={`question-label-${index}`}
          placeholder={t("Enter your question")}
          description={t("Keep it short and answerable.")}
          isDisabled={type === "update"}
        />
        <TextAreaFormikField
          name={`questions.${index}.description`}
          label={t("Description")}
          testID={`question-description-${index}`}
          isDisabled={type === "update"}
          placeholder={t("Add additional context or instructions (optional)")}
          description={t(
            "Use this for legal context or extra guidance for the applicant."
          )}
        />
        <Divider />
        {((type === "update" && !isEmpty(conditions)) ||
          (type === "create" && canShowConditional)) && (
          <>
            <Toggle
              testID={`question-conditional-toggle-${index}`}
              isSelected={!isEmpty(conditions)}
              isDisabled={type === "update"}
              onChange={(val: boolean) => {
                setFieldValue(
                  `questions.${index}.display_conditions`,
                  val
                    ? [{ parent_question_id: null, condition_value: null }]
                    : []
                );
              }}
            >
              {t("Conditional logic")}
            </Toggle>

            <Divider />
          </>
        )}
        {!isEmpty(conditions) && (
          <CustomQuestionnaireConditionalLogic
            type={type}
            values={values}
            currentQuestion={currentQuestion}
            index={index}
            setFieldValue={setFieldValue}
          />
        )}
        <Stack orientation="horizontal" align="center" justify="space-between">
          <PrimaryButton
            size="small"
            onPress={() => handleDone(currentQuestion)}
            testID={`question-done-${index}`}
            isDisabled={
              type === "update" ||
              !currentQuestion.data_type ||
              !currentQuestion.external_name ||
              (!isEmpty(conditions) &&
                (isEmpty(currentQuestion.display_conditions) ||
                  !every(currentQuestion.display_conditions, (obj) =>
                    every(obj, negate(isNil))
                  )))
            }
          >
            {t("Done")}
          </PrimaryButton>
          <LinkButton
            leftIcon="delete"
            textColor="text-base"
            hasUnderline={false}
            onPress={() => deleteQuestion(currentQuestion.id)}
            testID={`delete-question-${index}`}
            isDisabled={type === "update"}
          >
            {t("Delete")}
          </LinkButton>
        </Stack>
      </Stack>
    </ExpandableCard>
  );
};
